import type { ReactNode } from 'react';
import { ArrowUpRightIcon } from './Icons';

const motionId = (suffix: string) => `d61bd79a-9f99-ddf9-30ad-16bd9fb74${suffix}`;

const shortcuts = [
  { label: 'research', href: '/#modes', id: 'd3d', divider: 'd4a' },
  { label: 'engineering', href: '/#quality', id: 'd4b', divider: 'd58' },
  { label: 'prototyping', href: '/#manufacturing', id: 'd59', divider: 'd66' },
  { label: 'contact', href: '/#contact', id: 'd67', divider: 'd74', bottom: 'd75' },
] as const;

const legalLinks = [
  { label: 'privacy policy', href: '/privacy-policy', id: 'd97', divider: 'd9e', bottom: 'd9f' },
  { label: 'cookies policy', href: '/cookies-policy', id: 'da0', divider: 'da7', bottom: 'da8' },
  { label: 'terms of use', href: '/terms-of-use', id: 'da9', divider: 'db1', bottom: 'db2' },
] as const;

interface FooterLinkProps {
  label: string;
  href: string;
  id: string;
  divider: string;
  bottom?: string;
  number?: number;
  legal?: boolean;
}

function FooterLink({ label, href, id, divider, bottom, number, legal = false }: FooterLinkProps) {
  const icon = number ? (
    <div className="menu_icon-text">{number}</div>
  ) : (
    <div className="icon-embed-custom">
      <ArrowUpRightIcon size="small" />
    </div>
  );

  const content: ReactNode = (
    <>
      <div className="footer_element-hover" aria-hidden="true">
        <div className="footer_text-hover">{label}</div>
        {!legal && <div className="footer_icon-hover">{icon}</div>}
      </div>
      <div className="footer_element">
        <div className="footer_text">{label}</div>
        {!legal && (
          <div className="menu_icon" aria-hidden="true">
            {icon}
          </div>
        )}
      </div>
      <div data-motion-id={motionId(divider)} className="footer-divider" />
      {bottom && <div data-motion-id={motionId(bottom)} className="footer-divider is-bottom" />}
    </>
  );

  const className = `footer_element-wrapper${legal ? ' is-legal' : ''} w-inline-block`;
  return (
    <a
      className={className}
      data-motion-id={motionId(id)}
      href={href}
      target={legal ? '_blank' : undefined}
      rel={legal ? 'noopener' : undefined}
    >
      {content}
    </a>
  );
}

function FooterSignature({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`footer_logo-wrapper${mobile ? ' is-mobile' : ''}`}>
      <img
        src="/assets/images/Arcnova_Logo_Icon_Black.svg"
        loading="lazy"
        data-motion-id={mobile ? undefined : motionId('d35')}
        alt={mobile ? '' : 'Arcnova logo sign'}
      />
      <div className="text-size-tiny">© 2026 Arcnova — Ylabs LLC. All rights reserved.</div>
    </div>
  );
}

export function Footer() {
  return (
    <div className="footer_component">
      <footer data-motion-id={motionId('d19')} className="section_footer">
        <div className="padding-top padding-huge">
          <div className="container-full">
            <div className="content_footer">
              <div className="content_footer-top" aria-hidden="true">
                <div data-motion-id={motionId('d1e')} className="footer_animation-highlighter">
                  <div data-motion-id={motionId('d1f')} className="image_shading" />
                </div>
                <div className="footer_terrain-track">
                  {[0, 1, 2, 3].map((tile) => (
                    <div
                      key={tile}
                      className={`footer_terrain-tile${tile % 2 ? ' is-mirrored' : ''}`}
                    >
                      <img
                        src="/assets/images/mountains.svg"
                        width="1757"
                        height="626"
                        loading="lazy"
                        decoding="async"
                        alt=""
                        className="footer_animation-bg-grid"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="content_footer-bottom">
                <div className="content_footer-left">
                  <p className="footer_content-text">
                    <span data-word-reveal="true" className="heading-footer footer-prose">
                      For research enquiries, write to{' '}
                    </span>
                    <a
                      data-motion-id={motionId('d28')}
                      href="mailto:engage@arcnova.tech"
                      className="button_link w-inline-block"
                    >
                      <span className="button_link-hover" aria-hidden="true">
                        <span className="button_link-text footer-prose hover">
                          engage@arcnova.tech
                        </span>
                        <span className="button_link-icon-wrapper hover">
                          <span className="icon-embed-xxsmall">
                            <ArrowUpRightIcon />
                          </span>
                        </span>
                      </span>
                      <span className="button_link-text footer-prose">engage@arcnova.tech</span>
                      <span className="button_link-icon-wrapper">
                        <span className="button_link-icon">
                          <ArrowUpRightIcon />
                        </span>
                      </span>
                    </a>
                    <span data-word-reveal="true" className="heading-footer footer-prose">
                      {' '}
                      — research by Ylabs LLC.
                    </span>
                  </p>
                  <FooterSignature />
                </div>
                <div
                  id="w-node-d61bd79a-9f99-ddf9-30ad-16bd9fb74d38-9fb74d18"
                  data-motion-id={motionId('d38')}
                  className="content_footer-right"
                >
                  <div className="footer_content-part">
                    <div data-motion-id={motionId('d3a')} className="text-size-tiny">
                      shortcuts
                    </div>
                    <div className="footer_menu-wrapper">
                      {shortcuts.map((link, index) => (
                        <FooterLink key={link.href} {...link} number={index + 1} />
                      ))}
                    </div>
                  </div>
                  <div className="footer_content-part">
                    <div className="text-size-tiny">legal information</div>
                    <div className="footer_menu-wrapper is-legal">
                      {legalLinks.map((link) => (
                        <FooterLink key={link.href} {...link} legal />
                      ))}
                    </div>
                    <div className="text-size-tiny">
                      Sempacherstrasse 5, &nbsp;6003 Luzern Switzerland
                    </div>
                  </div>
                  <FooterSignature mobile />
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
